import React, { useState, useEffect, useRef } from 'react';
import { CreateCase } from './CreateCase';
import { CreateContact } from './CreateContact';
import '../styles/Cases.css';
import { useNavigate } from 'react-router';
import { ArrowDown, ArrowUp, Eye, Info, Lock, ChevronDown, Languages, X, Smile, Meh, Frown, SmilePlus, Bookmark, BookmarkMinus, LayoutList, Loader2 } from 'lucide-react';
import { CaseSidebar } from './CaseSidebar';
import { SearchBar } from './Nav';
import { useOptimizedResize } from '../utils/performance';
import { Checkbox, MultiSelect, SearchSelect } from './FieldComponents';
import { getContactInitialsClassName } from '../utils/colorUtils';
import Modal from './Modal';
import Alert from './Alert';
import { useNavVariant } from '../App';
import { Report } from '@mui/icons-material';
import { EntityHoverPreview } from './EntityHoverPreview';

export const Cases = ({ user }) => {
    const CASES_ENDPOINT = 'https://api.casedb.co/cases.php';
    const [createCase, setCreateCase] = useState(false);
    const [createContact, setCreateContact] = useState(false);
    const [cases, setCases] = useState([]);
    const [pinnedCases, setPinnedCases] = useState([]);
    const [displaySidebar, setDisplaySidebar] = useState(null);
    const nameRef = useRef(null);
    const navigate = useNavigate();
    const navVariant = useNavVariant();
    const [caseTemplates, setCaseTemplates] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [tagSearchQuery, setTagSearchQuery] = useState('');

    const [displayHeaders, setDisplayHeaders] = useState({
        case_name: true,
        phase: true,
        tags: true,
        case_type: window.innerWidth > 968,
        created_at: true,
        last_updated: true,
        first_primary: window.innerWidth > 1500
    });
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const [caseTypes, setCaseTypes] = useState([]);
    const [showMyCasesOnly, setShowMyCasesOnly] = useState(false);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 100,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
    });
    const [perPageOptions] = useState([25, 50, 100, 500, 1000]);
    const [filterOptions, setFilterOptions] = useState({
        case_types: [],
        phases: [],
        subphases: [],
        users: []
    });
    const [filters, setFilters] = useState({
        case_types: [],
        phases: [],
        subphases: [],
        first_primaries: [],
        referral_contacts: [],
        show_archived: false
    });
    const [referralContactOptions, setReferralContactOptions] = useState({});
    const [sortBy, setSortBy] = useState('updated_at');
    const [sortOrder, setSortOrder] = useState('DESC');
    const [isScrolled, setIsScrolled] = useState(false);
    const [showNatureModal, setShowNatureModal] = useState(false);
    const [selectedNature, setSelectedNature] = useState(null);
    const [natureDescription, setNatureDescription] = useState('');
    const [selectedContactId, setSelectedContactId] = useState(null);
    const [message, setMessage] = useState({ message: '', type: '' });
    const [isCasesLoading, setIsCasesLoading] = useState(false);
    const [casesFetchFailed, setCasesFetchFailed] = useState(false);

    useOptimizedResize(() => {
        setScreenWidth(window.innerWidth);
    });

    useOptimizedResize(() => {
        const width = window.innerWidth;
        setDisplayHeaders({
            case_name: true,
            phase: true,
            tags: width > 768,
            case_type: width > 968,
            created_at: width > 1350,
            last_updated: width > 1150,
            first_primary: width > 1500
        });
    });

    useEffect(() => {
        if (new URLSearchParams(window.location.search).get("new") === "true") {
            setCreateCase(true);
        }

        document.title = "Cases | CaseDB"
    }, []);

    const didInitialFetchRef = useRef(false);
    const prevSearchRef = useRef(searchQuery);
    const prevTagSearchRef = useRef(tagSearchQuery);

    useEffect(() => {
        const isSearchChange = prevSearchRef.current !== searchQuery;
        const isTagSearchChange = prevTagSearchRef.current !== tagSearchQuery;

        prevSearchRef.current = searchQuery;
        prevTagSearchRef.current = tagSearchQuery;

        if (!didInitialFetchRef.current) {
            didInitialFetchRef.current = true;
            fetchCases(1, pagination.per_page);
            return;
        }

        setDisplaySidebar(null); // Hide sidebar when filters/search change

        if (isSearchChange || isTagSearchChange) {
            const timeoutId = setTimeout(() => {
                fetchCases(1, pagination.per_page); // Reset to page 1 when search changes
            }, 300);

            return () => clearTimeout(timeoutId);
        }

        fetchCases(1, pagination.per_page); // Reset to page 1 for other changes
    }, [searchQuery, tagSearchQuery, filters, sortBy, sortOrder, showMyCasesOnly, pagination.per_page]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.total_pages) {
            fetchCases(newPage, pagination.per_page);
        }
    };

    const handlePerPageChange = (newPerPage) => {
        fetchCases(1, newPerPage);
    };

    const handleFilterChange = (filterType, value, checked) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: checked 
                ? [...prev[filterType], value]
                : prev[filterType].filter(item => item !== value)
        }));
    };

    const handleShowArchivedChange = (checked) => {
        setFilters(prev => ({
            ...prev,
            show_archived: checked
        }));
    };

    const hasActiveFilters = () => {
        const filterArrays = [
            filters.case_types,
            filters.phases,
            filters.subphases,
            filters.first_primaries,
            filters.referral_contacts
        ];

        const hasArrayFilters = filterArrays.some(arr => Array.isArray(arr) && arr.length > 0);
        const hasSearch = Boolean(searchQuery?.trim() || tagSearchQuery?.trim());
        const hasArchived = Boolean(filters.show_archived);
        const hasMyCases = Boolean(showMyCasesOnly);

        return hasArrayFilters || hasSearch || hasArchived || hasMyCases;
    };

    const buildReportCriteria = () => {
        const conditions = [];

        if (filters.case_types.length > 0) {
            conditions.push({
                field_id: 'leads.case_type_id',
                operator: '=',
                value: filters.case_types
            });
        }

        if (filters.phases.length > 0) {
            conditions.push({
                field_id: 'cases.phase_id',
                operator: '=',
                value: filters.phases
            });
        }

        if (filters.subphases.length > 0) {
            conditions.push({
                field_id: 'cases.subphase_id',
                operator: '=',
                value: filters.subphases
            });
        }

        if (filters.first_primaries.length > 0) {
            conditions.push({
                field_id: "cases.team['1'][0]",
                operator: '=',
                value: filters.first_primaries
            });
        }

        if (filters.referral_contacts.length > 0) {
            conditions.push({
                field_id: 'leads.referral_contact',
                operator: '=',
                value: filters.referral_contacts
            });
        }

        if (searchQuery?.trim()) {
            conditions.push({
                field_id: 'cases.case_name',
                operator: 'LIKE',
                value: searchQuery.trim()
            });
        }

        if (tagSearchQuery?.trim()) {
            conditions.push({
                field_id: 'cases.tags',
                operator: 'LIKE',
                value: tagSearchQuery.trim()
            });
        }

        if (showMyCasesOnly && user?.id) {
            conditions.push({
                field_id: "cases.team['1'][0]",
                operator: '=',
                value: String(user.id)
            });
        }

        if (!filters.show_archived) {
            const archivedPhase = filterOptions.phases?.find(phase => String(phase.phase).toLowerCase() === 'archived');
            if (archivedPhase?.id) {
                conditions.push({
                    field_id: 'cases.phase_id',
                    operator: '!=',
                    value: String(archivedPhase.id)
                });
            }
        }

        return {
            logic: 'AND',
            conditions
        };
    };

    const buildReportFields = () => {
        const fields = [
            'cases.id',
            'cases.case_name',
            'cases.phase_id',
            'cases.subphase_id',
            'leads.case_type_id',
            'leads.referral_contact',
            "cases.team['1'][0]"
        ];

        return fields;
    };

    const handleGenerateReport = async () => {
        try {
            const response = await fetch('https://api.casedb.co/report-update.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    action: 'save_as',
                    report_name: 'List of Cases - Generated Report',
                    report_type: 'cases',
                    fields: buildReportFields(),
                    criteria: buildReportCriteria()
                })
            });

            const data = await response.json();
            if (!data.success) {
                setMessage({ message: data.message || data.error || 'Failed to generate report', type: 'error' });
                console.error('Failed to generate report:', data.message || data.error);
            } else {
                setMessage({ message: 'Report generated successfully', type: 'success' });
            }
        } catch (error) {
            setMessage({ message: 'Error generating report', type: 'error' });
            console.error('Error generating report:', error);
        }
    };

    const handleSortChange = (column) => {
        if (sortBy === column) {
            setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(column);
            setSortOrder('DESC');
        }
    };

    const clearAllFilters = () => {
        setFilters({
            case_types: [],
            phases: [],
            subphases: [],
            first_primaries: [],
            referral_contacts: [],
            show_archived: false
        });
        setSortBy('updated_at');
        setSortOrder('DESC');
    };

    const handleOpenNatureModal = (e, contactId, nature, description) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedContactId(contactId);
        setSelectedNature(nature || null);
        setNatureDescription(description || '');
        setShowNatureModal(true);
    };

    const handleSaveNature = async () => {
        try {
            const response = await fetch('https://api.casedb.co/contacts.php', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    contact_id: selectedContactId,
                    nature: selectedNature,
                    nature_description: natureDescription
                })
            });

            const data = await response.json();
            if (data.success) {
                setShowNatureModal(false);
                fetchCases(pagination.current_page, pagination.per_page);
            } else {
                console.error('Failed to update nature');
            }
        } catch (error) {
            console.error('Error updating nature:', error);
        }
    };

    const fetchCases = async (page = 1, perPage = pagination.per_page) => {
        setIsCasesLoading(true);
        setCasesFetchFailed(false);

        try {
            const params = new URLSearchParams({
                page: page.toString(),
                per_page: perPage.toString(),
                sort_by: sortBy,
                sort_order: sortOrder,
                time: new Date().getTime().toString()
            });
            
            if (searchQuery) {
                params.append('search', searchQuery);
            }
            
            if (tagSearchQuery) {
                params.append('tag_search', tagSearchQuery);
            }
            
            if (filters.case_types.length > 0) {
                params.append('case_types', filters.case_types.join(','));
            }
            
            if (filters.phases.length > 0) {
                params.append('phases', filters.phases.join(','));
            }
            
            if (filters.subphases.length > 0) {
                params.append('subphases', filters.subphases.join(','));
            }
            
            if (filters.first_primaries.length > 0) {
                params.append('first_primaries', filters.first_primaries.join(','));
            }

            if (filters.referral_contacts.length > 0) {
                params.append('referral_contacts', filters.referral_contacts.join(','));
            }
            
            if (filters.show_archived) {
                params.append('show_archived', 'true');
            }
            
            if (showMyCasesOnly) {
                params.append('my_cases_only', 'true');
            }
            
            const response = await fetch(`${CASES_ENDPOINT}?${params}`, {
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch cases: ${response.status}`);
            }
            
            const data = await response.json();

            if (!data.success) {
                throw new Error(data.message || data.error || 'Failed to fetch cases');
            }

            setCases(data.cases || []);
            setPinnedCases(Number(page) === 1 ? (data.pinned_cases || []) : []);

            if (data.pagination) {
                setPagination(data.pagination);
            }

            if (data.filter_options) {
                setFilterOptions(prev => ({
                    ...prev,
                    ...data.filter_options,
                    // Preserve subphases from our separate phases API call
                    subphases: prev.subphases || []
                }));
            }
        } catch (error) {
            console.error("Error fetching cases:", error);
            setCases([]);
            setPinnedCases([]);
            setCasesFetchFailed(true);
        } finally {
            setIsCasesLoading(false);
        }
    };

    const handleTogglePin = async (caseId, isPinned) => {
        try {
            const response = await fetch('https://api.casedb.co/cases.php?pin=true', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    case_id: caseId,
                    pinned: !isPinned
                })
            });

            const data = await response.json();
            if (data.success) {
                fetchCases(pagination.current_page, pagination.per_page);
            } else {
                console.error('Failed to update pin state:', data.message || data.error);
            }
        } catch (error) {
            console.error('Error updating pin state:', error);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString || typeof dateString !== 'string') return ["", "", "", "", "", ""];

        const months = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];

        try {
            const hasTimeZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(dateString);
            const isDateTimeWithSpace = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString);
            const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
            const isIsoWithoutZone = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(dateString);

            let normalized = dateString;
            if (!hasTimeZone) {
                if (isDateTimeWithSpace) {
                    normalized = `${dateString.replace(' ', 'T')}Z`;
                } else if (isDateOnly) {
                    normalized = `${dateString}T00:00:00Z`;
                } else if (isIsoWithoutZone) {
                    normalized = `${dateString}Z`;
                }
            }

            let parsedDate = new Date(normalized);

            if (isNaN(parsedDate.getTime())) {
                parsedDate = new Date(`${dateString}T00:00:00Z`);
            }

            if (isNaN(parsedDate.getTime())) {
                return ["", "", "", "", "", ""];
            }

            const fallbackTimeZone = "America/Chicago";
            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || fallbackTimeZone;
            let zonedTime;
            try {
                zonedTime = new Date(parsedDate.toLocaleString("en-US", { timeZone: userTimeZone }));
            } catch (e) {
                zonedTime = new Date(parsedDate.toLocaleString("en-US", { timeZone: fallbackTimeZone }));
            }

            const iso = parsedDate.toISOString();
            const longDate = `${months[zonedTime.getMonth()]} ${zonedTime.getDate()}, ${zonedTime.getFullYear()}`;
            const shortDate = `${zonedTime.getMonth() + 1}/${zonedTime.getDate()}/${zonedTime.getFullYear()}`;
            const shortFormatted = `${zonedTime.getDate()} ${months[zonedTime.getMonth()].slice(0, 3)} ${zonedTime.getFullYear()}`;
            const hours = zonedTime.getHours();
            const minutes = zonedTime.getMinutes();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const hour12 = hours % 12 || 12;
            const minutePadded = String(minutes).padStart(2, '0');
            const timeFormatted = `${hour12}:${minutePadded} ${ampm}`;
            const dateTimeFormatted = `${zonedTime.getMonth() + 1}/${zonedTime.getDate()}/${zonedTime.getFullYear()} ${timeFormatted}`;

            return [longDate, iso, timeFormatted, shortDate, shortFormatted, dateTimeFormatted];
        } catch (e) {
            console.error("Invalid date:", dateString, e);
            return ["", "", "", "", "", ""];
        }
    };

    const handleNewContactCreated = () => {
        // Refresh cases to get updated contact information
        fetchCases(pagination.current_page, pagination.per_page);
        setCreateContact(false);
    };

    const handleMouseMove = (event) => {
        setTooltipPos({ x: event.clientX + 10, y: event.clientY });
    };

    const handleClick = (id) => {
        setDisplaySidebar((prevId) => (prevId === id ? null : id));
    };
    
    const fetchCaseTemplates = async () => {
        try {
            const response = await fetch("https://api.casedb.co/layout.php");

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setCaseTemplates(data.templates);

        } catch (error) {
            console.error("Error fetching case templates:", error);
        }
    };

    const fetchCaseTypes = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/custom_fields.php?time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            setCaseTypes(data.case_types);
        } catch (error) {
            console.error("Error fetching case types:", error);
        }
    };

    const fetchReferralContacts = async () => {
        try {
            const response = await fetch('https://api.casedb.co/contacts.php?list=true', { credentials: 'include' });
            const data = await response.json();
            if (data.success && Array.isArray(data.contacts)) {
                const options = data.contacts.reduce((acc, contact) => {
                    if (contact?.id && contact?.name) {
                        acc[String(contact.id)] = contact.name;
                    }
                    return acc;
                }, {});
                setReferralContactOptions(options);
            }
        } catch (error) {
            console.error('Error fetching referral contacts:', error);
        }
    };

    const fetchPhases = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/phases.php`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                // Update filter options to include both phases and subphases
                setFilterOptions(prev => ({
                    ...prev,
                    phases: data.phases || [],
                    subphases: data.subphases || []
                }));
            }
        } catch (error) {
            console.error("Error fetching phases and subphases:", error);
        }
    };

    useEffect(() => {
        fetchCaseTemplates();
        fetchCaseTypes();
        fetchPhases();
        fetchReferralContacts();
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 0);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const pinnedFirst = pagination.current_page === 1 ? pinnedCases : [];
    const pinnedIds = new Set(pinnedFirst.map(c => c.case_id));
    const displayedCases = pinnedFirst.length > 0
        ? [...pinnedFirst, ...cases.filter(c => !pinnedIds.has(c.case_id))]
        : cases;
    const showPhaseColumn = screenWidth >= 768 && displayHeaders.phase;
    const showCaseTypeColumn = screenWidth > 968 && displayHeaders.case_type;
    const showUpdatedColumn = screenWidth > 1150 && displayHeaders.last_updated;
    const showCreatedColumn = screenWidth > 1350 && displayHeaders.created_at;
    const showFirstPrimaryColumn = screenWidth > 1500 && displayHeaders.first_primary;
    const visibleSecondaryCaseColumnCount = [showPhaseColumn, showCaseTypeColumn, showFirstPrimaryColumn, showCreatedColumn, showUpdatedColumn].filter(Boolean).length;
    const visibleCaseColumnCount = 1 + visibleSecondaryCaseColumnCount;
    const desktopCaseNameWidth = showFirstPrimaryColumn ? 40 : showCreatedColumn ? 44 : showUpdatedColumn ? 46 : showCaseTypeColumn ? 50 : 60;
    const phaseColumnWeight = showPhaseColumn ? 1.35 : 0;
    const secondaryColumnWeight = Math.max(visibleSecondaryCaseColumnCount - (showPhaseColumn ? 1 : 0), 0);
    const totalSecondaryColumnWeight = phaseColumnWeight + secondaryColumnWeight;
    const secondaryDesktopCaseColumnWidth = totalSecondaryColumnWeight > 0 ? (100 - desktopCaseNameWidth) / totalSecondaryColumnWeight : 0;
    const phaseDesktopCaseColumnWidth = secondaryDesktopCaseColumnWidth * phaseColumnWeight;
    const mobileCaseColumnWidth = screenWidth < 768 ? `${100 / visibleCaseColumnCount}%` : null;
    const caseNameColumnStyle = { width: mobileCaseColumnWidth || `${desktopCaseNameWidth}%` };
    const secondaryCaseColumnStyle = { width: mobileCaseColumnWidth || `${secondaryDesktopCaseColumnWidth}%` };
    const phaseCaseColumnStyle = { width: mobileCaseColumnWidth || `${phaseDesktopCaseColumnWidth}%` };
    const isMobileFilters = screenWidth < 768;

    return (
        <div className={`page-container case-page ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
            <div id='page-header'>
                <h2>Cases</h2>
                <button className='action' onClick={() => setCreateCase(true)}>Create Case</button>
                <div className='active-cases subtext'>
                    <p>Cases: {pagination.total || cases.length}</p> 
                </div>
            </div>
            <div className='case-filters'>
                <div className={`filters-row ${isMobileFilters ? 'mobile-case-filter-row' : ''}`}>
                    <SearchBar 
                        expanded={true} 
                        setExpanded={() => {}} 
                        placeholder={`Search cases`}
                        setSearchQuery={setSearchQuery}
                        autofocus={!isMobileFilters}
                    />

                    {/* Show Archived */}
                    <Checkbox
                        label="Show Archived"
                        value={filters.show_archived}
                        onChange={() => handleShowArchivedChange(!filters.show_archived)}
                    />

                    {!isMobileFilters && (
                        <>
                            <SearchBar 
                                expanded={true} 
                                setExpanded={() => {}} 
                                placeholder={`Search tags`}
                                setSearchQuery={setTagSearchQuery}
                            />
                            
                            {/* Sort Controls */}
                            <div className="sort-controls">
                                <div className="filter-group">
                                    <div className="select-wrapper">
                                        <select 
                                            className="filter-select"
                                            value={sortBy}
                                            onChange={(e) => setSortBy(e.target.value)}
                                        >
                                            <option value="updated_at">Last Updated</option>
                                            <option value="create_date">Created Date</option>
                                            <option value="case_name">Case Name</option>
                                            <option value="phase_name">Phase</option>
                                            <option value="first_primary">First Primary</option>
                                            <option value="case_type_name">Case Type</option>
                                        </select>
                                        <ChevronDown size={16} className="select-chevron" />
                                    </div>
                                </div>
                                
                                <button 
                                    className="form-box alt"
                                    onClick={() => setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC')}
                                >
                                    {sortOrder === 'ASC' ? <ArrowDown size={16} /> : <ArrowUp size={16} />}
                                </button>
                            </div>

                            <div className="filter-group">
                                <div className="select-wrapper">
                                    <select 
                                        className='filter-select' 
                                        value={pagination.per_page} 
                                        onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
                                    >
                                        {perPageOptions.map(option => (
                                            <option key={option} value={option}>{option} per page</option>
                                        ))}
                                    </select>
                                    <ChevronDown size={16} className="select-chevron" />
                                </div>
                            </div>

                            <div
                                className='my-cases'
                                style={showMyCasesOnly ? {backgroundColor: 'var(--fill)'} : {}}
                                onClick={() => setShowMyCasesOnly(prev => !prev)}
                                title="Show my cases only"
                            >
                                <Eye size={18} style={{ color: showMyCasesOnly ? 'var(--fill-2)' : 'var(--secondary-color)' }} />
                            </div>

                            {hasActiveFilters() && (
                                <div className='action tertiary' title="Generate Report with Active Filters" onClick={handleGenerateReport}>
                                    <LayoutList size={20} />
                                </div>
                            )}
                        </>
                    )}

                </div>

                {!isMobileFilters && <div className="filters-row advanced-filters">
                    <div className="filter-group">
                        <div className="select-wrapper">
                            <select 
                                className='filter-select' 
                                value="" 
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value && !filters.case_types.includes(value)) {
                                        setFilters(prev => ({
                                            ...prev,
                                            case_types: [...prev.case_types, value]
                                        }));
                                    }
                                }}
                            >
                                <option value="">Case Types</option>
                                {filterOptions.case_types.map(caseType => (
                                    <option key={caseType.id} value={caseType.id}>
                                        {caseType.name}
                                    </option>
                                ))}
                            </select>
                            <ChevronDown size={16} className="select-chevron" />
                        </div>
                    </div>

                    {/* Referral Contact Filter */}
                    <div className="filter-group">
                        <SearchSelect
                            value={filters.referral_contacts[0] ?? null}
                            onChange={(value) => setFilters(prev => ({
                                ...prev,
                                referral_contacts: value ? [value] : []
                            }))}
                            options={referralContactOptions}
                            placeholder="Referral Contact"
                        />
                    </div>

                    {/* Phases Filter */}
                    <div className="filter-group">
                        <div className="select-wrapper">
                            <select 
                                className='filter-select' 
                                value="" 
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value && !filters.phases.includes(value)) {
                                        setFilters(prev => ({
                                            ...prev,
                                            phases: [...prev.phases, value]
                                        }));
                                    }
                                }}
                            >
                                <option value="">Phases</option>
                                {filterOptions.phases.map(phase => (
                                    <option key={phase.id} value={phase.id}>
                                        {phase.phase}
                                    </option>
                                ))}
                            </select>
                            <ChevronDown size={16} className="select-chevron" />
                        </div>
                    </div>

                    {/* Subphases Filter */}
                    <div className="filter-group">
                        <div className="select-wrapper">
                            <select 
                                className='filter-select' 
                                value="" 
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value && !filters.subphases.includes(value)) {
                                        setFilters(prev => ({
                                            ...prev,
                                            subphases: [...prev.subphases, value]
                                        }));
                                    }
                                }}
                            >
                                <option value="">Subphases</option>
                                {filterOptions.subphases?.map(subphase => (
                                    <option key={subphase.id} value={subphase.id}>
                                        {subphase.phase}
                                    </option>
                                )) || []}
                            </select>
                            <ChevronDown size={16} className="select-chevron" />
                        </div>
                    </div>

                    {/* First Primary Filter */}
                    <div className="filter-group">
                        <div className="select-wrapper">
                            <select 
                                className='filter-select' 
                                value="" 
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value && !filters.first_primaries.includes(value)) {
                                        setFilters(prev => ({
                                            ...prev,
                                            first_primaries: [...prev.first_primaries, value]
                                        }));
                                    }
                                }}
                            >
                                <option value="">Primary</option>
                                {filterOptions.users.map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.name}
                                    </option>
                                ))}
                            </select>
                            <ChevronDown size={16} className="select-chevron" />
                        </div>
                    </div>
                </div>}

                {/* Active Filters Display */}
                {!isMobileFilters && (filters.case_types.length > 0 || filters.phases.length > 0 || filters.subphases.length > 0 || filters.first_primaries.length > 0 || filters.referral_contacts.length > 0 || filters.show_archived) && (
                    <div className="active-filters-row">
                        {/* Case Type Filters */}
                        {filters.case_types.map(caseTypeId => {
                            const caseType = filterOptions.case_types.find(ct => ct.id == caseTypeId);
                            return caseType ? (
                                <div key={`case-type-${caseTypeId}`} className="filter-tag">
                                    {caseType.name}
                                    <X 
                                        className="filter-tag-remove"
                                        onClick={() => setFilters(prev => ({
                                            ...prev,
                                            case_types: prev.case_types.filter(id => id !== caseTypeId)
                                        }))}
                                    />
                                </div>
                            ) : null;
                        })}

                        {/* Phase Filters */}
                        {filters.phases.map(phaseId => {
                            const phase = filterOptions.phases.find(p => p.id == phaseId);
                            return phase ? (
                                <div key={`phase-${phaseId}`} className="filter-tag">
                                    Phase: {phase.phase}
                                    <X 
                                        className="filter-tag-remove"
                                        onClick={() => setFilters(prev => ({
                                            ...prev,
                                            phases: prev.phases.filter(id => id !== phaseId)
                                        }))}
                                    />
                                </div>
                            ) : null;
                        })}

                        {/* Subphase Filters */}
                        {filters.subphases?.map(subphaseId => {
                            const subphase = filterOptions.subphases?.find(s => s.id == subphaseId);
                            return subphase ? (
                                <div key={`subphase-${subphaseId}`} className="filter-tag">
                                    Subphase: {subphase.phase}
                                    <X 
                                        className="filter-tag-remove"
                                        onClick={() => setFilters(prev => ({
                                            ...prev,
                                            subphases: prev.subphases.filter(id => id !== subphaseId)
                                        }))}
                                    />
                                </div>
                            ) : null;
                        }) || []}

                        {/* First Primary Filters */}
                        {filters.first_primaries.map(userId => {
                            const user = filterOptions.users.find(u => u.id == userId);
                            return user ? (
                                <div key={`user-${userId}`} className="filter-tag">
                                    {user.name}
                                    <X 
                                        className="filter-tag-remove"
                                        onClick={() => setFilters(prev => ({
                                            ...prev,
                                            first_primaries: prev.first_primaries.filter(id => id !== userId)
                                        }))}
                                    />
                                </div>
                            ) : null;
                        })}

                        {/* Referral Contact Filter */}
                        {filters.referral_contacts.map(contactId => {
                            const contactName = referralContactOptions?.[contactId];
                            return contactName ? (
                                <div key={`referral-${contactId}`} className="filter-tag">
                                    Referral: {contactName}
                                    <X 
                                        className="filter-tag-remove"
                                        onClick={() => setFilters(prev => ({
                                            ...prev,
                                            referral_contacts: prev.referral_contacts.filter(id => id !== contactId)
                                        }))}
                                    />
                                </div>
                            ) : null;
                        })}

                        {/* Show Archived Filter */}
                        {filters.show_archived && (
                            <div className="filter-tag">
                                Show Archived
                                <X 
                                    className="filter-tag-remove"
                                    onClick={() => handleShowArchivedChange(false)}
                                />
                            </div>
                        )}

                        {/* Clear All Filters */}
                        <span onClick={() => {
                            clearAllFilters();
                        }}>
                            Clear filters
                        </span>
                    </div>
                )}
            </div>
            
            {/* Pagination Controls */}
            <div className='pagination-controls' style={{ justifyContent: 'center', marginTop: "10px", padding: "10px 0 0 0" }}>
                <div className='pagination-button-row'>
                    <button 
                        className='action alt' 
                        disabled={!pagination.has_prev}
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                    >
                        Previous
                    </button>
                    <button 
                        className='action alt' 
                        disabled={!pagination.has_next}
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                    >
                        Next
                    </button>
                </div>
                <span className='pagination-info subtext'>
                    Page {pagination.current_page} of {pagination.total_pages} ({pagination.total} total cases)
                </span>
            </div>
            <div className='case-page-container'>
                <table className='cases-table'>
                    <thead>
                        <tr>
                            <th className='case-name sortable' style={{ paddingLeft: '5px', ...caseNameColumnStyle }} onClick={() => handleSortChange('case_name')}>
                                Case Name {sortBy === 'case_name' && (sortOrder === 'ASC' ? '↑' : '↓')}
                            </th>
                            {showPhaseColumn && <th className='sortable' style={phaseCaseColumnStyle} onClick={() => handleSortChange('phase_name')}>
                                Phase {sortBy === 'phase_name' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                <Info 
                                    size={16} 
                                    className='cases-phase-info-icon'
                                    onMouseEnter={() => setShowTooltip(true)}
                                    onMouseMove={handleMouseMove}
                                    onMouseLeave={() => setShowTooltip(false)}
                                />
                            </th>}
                            {showCaseTypeColumn && <th className='sortable' style={secondaryCaseColumnStyle} onClick={() => handleSortChange('case_type_name')}>
                                Case Type {sortBy === 'case_type_name' && (sortOrder === 'ASC' ? '↑' : '↓')}
                            </th>}
                            {showFirstPrimaryColumn && <th className='sortable' style={secondaryCaseColumnStyle} onClick={() => handleSortChange('first_primary')}>
                                Primary {sortBy === 'first_primary' && (sortOrder === 'ASC' ? '↑' : '↓')}
                            </th>}
                            {showCreatedColumn && <th className='sortable' style={secondaryCaseColumnStyle} onClick={() => handleSortChange('create_date')}>
                                Created {sortBy === 'create_date' && (sortOrder === 'ASC' ? '↑' : '↓')}
                            </th>}
                            {showUpdatedColumn && <th className='sortable' style={secondaryCaseColumnStyle} onClick={() => handleSortChange('updated_at')}>
                                Last Updated {sortBy === 'updated_at' && (sortOrder === 'ASC' ? '↑' : '↓')}
                            </th>}
                        </tr>
                    </thead>
                    <tbody>
                        {isCasesLoading ? (
                            <tr className='subtext' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <td colSpan={visibleCaseColumnCount}>
                                    <span className='subtext' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
                                        Loading Cases <Loader2 size={16} className="spinner" />
                                    </span>
                                </td>
                            </tr>
                        ) : displayedCases.length > 0 ? displayedCases.map((c, index) => (
                            <EntityHoverPreview
                                as="tr"
                                entityType="case"
                                entityId={c?.case_id}
                                key={index}
                                className={`case ${displaySidebar === c.case_id ? 'selected' : ''}`}
                                onClick={() => {
                                    if (showCreatedColumn) handleClick(c.case_id);
                                    else navigate(`/case/${c.case_id}`);
                                }}
                            >
                                <td className='case-name' title={`Case ID: ${c?.case_id}`} ref={nameRef} style={caseNameColumnStyle}>
                                    <a
                                        href={`/case/${c?.case_id}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate(`/case/${c?.case_id}`);
                                        }}
                                        className='case-name-link'
                                        style={{ width: '100%' }}
                                    >
                                            <div className="case-name-content">
                                                {c?.contact_display?.includes('contact-media.php') || c?.contact_display?.includes('uploads') || c?.contact_display?.startsWith('http')
                                                    ? <img className={getContactInitialsClassName(c?.case_id)} src={c?.contact_display?.startsWith('http') ? c?.contact_display : `https://api.casedb.co/${c?.contact_display}`} alt="Profile" />
                                                    : <span className={getContactInitialsClassName(c?.case_id)}>{c?.contact_display || c?.case_name?.charAt(0) || ''}</span>
                                                }
                                            </div>
                                            <span className='case-name-title'>{c.case_name}</span>
                                            {c?.contact_language && c.contact_language.toLowerCase() !== 'english' && (
                                                <Languages
                                                    size={16}
                                                    className="language-indicator"
                                                    title={`Needs an interpreter for ${c.contact_language}`}
                                                />
                                            )}
                                            <div className='case-name-actions'>
                                                <div className='contact-nature' onClick={(e) => handleOpenNatureModal(e, c?.contact_id, c?.contact_nature, c?.contact_nature_description)} style={{ cursor: 'pointer' }}>
                                                    {Number(c?.contact_nature) === 1 ? <Smile size={16} className="contact-nature-icon well-natured" /> : Number(c?.contact_nature) === 2 ? <Meh size={16} className="contact-nature-icon neutral-natured" /> : Number(c?.contact_nature) === 3 ? <Frown size={16} className="contact-nature-icon ill-natured" /> : <SmilePlus size={16} className="contact-nature-icon subtext" />}
                                                </div>
                                                <span
                                                    className={`pin-case ${Number(c?.pinned) === 1 ? 'pinned' : ''}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleTogglePin(c.case_id, Number(c?.pinned) === 1);
                                                    }}
                                                    title={Number(c?.pinned) === 1 ? 'Unpin case' : 'Pin case'}
                                                >
                                                    {Number(c?.pinned) === 1 ? (
                                                        <BookmarkMinus size={18} color="var(--fill)" />
                                                    ) : (
                                                        <Bookmark size={18} />
                                                    )}
                                                </span>
                                            </div>
                                            {screenWidth >= 768 &&
                                                <>
                                                    {JSON.parse(c?.tags)?.slice(0, 2)?.map((value) => (
                                                        <span className='tag caps' key={value}>
                                                            {value.includes('#') ? value : `#${value}`}
                                                        </span>
                                                    ))}
                                                    {JSON.parse(c?.tags)?.length > 2 && (
                                                        <span className='tag more-tags caps' title={JSON.parse(c?.tags)?.slice(2).join(', ')}>
                                                            +{JSON.parse(c?.tags)?.length - 2} more
                                                        </span>
                                                    )}
                                                </>
                                            }
                                    </a>
                                </td>
                                {showPhaseColumn &&
                                    <td className='default small-text' style={phaseCaseColumnStyle}>
                                        {c.phase_name}
                                        {Number(c.phase_id) === 22 ? (
                                            <span className="subphase-indicator">&nbsp;/ {c.subphase_name || 'Unknown'}</span>
                                        ) : c.subphase_name ? (
                                            <span className="subphase-indicator">&nbsp;/ {c.subphase_name}</span>
                                        ) : null}
                                    </td>
                                }
                                {showCaseTypeColumn && <td className='default small-text case-meta-cell' style={secondaryCaseColumnStyle} title={c.case_type_name || ''}>{c.case_type_name || ''}</td>}
                                {showFirstPrimaryColumn && <td className='default small-text case-meta-cell' style={secondaryCaseColumnStyle} title={c.first_primary || ''}>{c.first_primary || ''}</td>}
                                {showCreatedColumn && <td className='default small-text' style={secondaryCaseColumnStyle}>{formatDate(c.create_date)[0]}</td>}
                                {showUpdatedColumn && <td className='default small-text' style={secondaryCaseColumnStyle}>{formatDate(c.updated_at)[0]}, {formatDate(c.updated_at)[2]}</td>}
                            </EntityHoverPreview>
                        )) : casesFetchFailed ? (
                            <tr className='subtext' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <td colSpan={visibleCaseColumnCount} className='default' style={{ textAlign: 'center' }}>
                                    No cases found
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
                {showTooltip && (
                    <div className="cases-phase-tooltip" style={{ top: `${tooltipPos.y}px`, left: `${tooltipPos.x}px` }}>
                    </div>
                )}
                {displaySidebar && displayedCases.some(c => c.case_id === displaySidebar) && (
                    <CaseSidebar 
                        key={displaySidebar} 
                        id={displaySidebar} 
                        cases={displayedCases} 
                        caseTemplates={caseTemplates} 
                        fetchCases={() => fetchCases(pagination.current_page, pagination.per_page)} 
                        caseTypes={caseTypes} 
                        formatDate={formatDate}
                        onCreateNewContact={() => setCreateContact(true)}
                        isScrolled={isScrolled}
                    />
                )}
            </div>
            {/* Pagination Controls */}
            <div className='pagination-controls' style={{ justifyContent: 'center', marginTop: "10px", padding: "10px 0 0 0" }}>
                <div className='pagination-button-row'>
                    <button 
                        className='action alt' 
                        disabled={!pagination.has_prev}
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                    >
                        Previous
                    </button>
                    <button 
                        className='action alt' 
                        disabled={!pagination.has_next}
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                    >
                        Next
                    </button>
                </div>
                <span className='pagination-info subtext'>
                    Page {pagination.current_page} of {pagination.total_pages} ({pagination.total} total cases)
                </span>
            </div>
            {createCase && (
                <CreateCase 
                    user={user} 
                    setCreateCase={setCreateCase} 
                    fetchCases={() => fetchCases(pagination.current_page, pagination.per_page)} 
                    caseTemplates={caseTemplates} 
                />
            )}
            {createContact && (
                <CreateContact 
                    setCreateContact={setCreateContact} 
                    onContactCreated={handleNewContactCreated}
                />
            )}
            {showNatureModal && (
                <Modal
                    title="Contact Nature"
                    onClose={() => setShowNatureModal(false)}
                    footer={
                        <div className='modal-footer-actions'>
                            <button
                                onClick={() => setShowNatureModal(false)}
                                className="action alt"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveNature}
                                className="action"
                                disabled={selectedNature === null}
                            >
                                Save
                            </button>
                        </div>
                    }
                >
                    <div className="modal-content-wrapper">
                        <div className='smiles-container'>
                            <div
                                onClick={() => setSelectedNature(1)}
                                className={`smile-container ${selectedNature === 1 ? 'selected' : ''}`}
                            >
                                <Smile size={30} className="contact-nature-icon well-natured" />
                                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px' }}>Well Natured</div>
                            </div>
                            <div
                                onClick={() => setSelectedNature(2)}
                                className={`smile-container ${selectedNature === 2 ? 'selected' : ''}`}
                            >
                                <Meh size={30} className="contact-nature-icon neutral-natured" />
                                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px' }}>Neutral</div>
                            </div>
                            <div
                                onClick={() => setSelectedNature(3)}
                                className={`smile-container ${selectedNature === 3 ? 'selected' : ''}`}
                            >
                                <Frown size={30} className="contact-nature-icon ill-natured" />
                                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px' }}>Ill Natured</div>
                            </div>
                        </div>

                        <div className='form-group full' style={{ maxWidth: '100%', width: '100%', marginBottom: '0' }}>
                            <label className="subtext">
                                Nature Description
                            </label>
                            <textarea
                                value={natureDescription}
                                onChange={(e) => setNatureDescription(e.target.value)}
                                placeholder="Add notes about this contact's nature..."
                            />
                        </div>
                    </div>
                </Modal>
            )}
            {message.message.length > 0 && (
                <Alert
                    message={message.message}
                    type={message.type}
                    onClose={() => setMessage({ message: '', type: '' })}
                />
            )}
        </div>
    );
};
